import type { PublicEvent } from '@irace/shared';

interface Branding {
  banner_url?: unknown;
  banner_alt?: unknown;
}

function bannerFrom(event: PublicEvent): { url: string; alt: string } | null {
  const branding = event.branding as Branding | undefined;
  const url = typeof branding?.banner_url === 'string' ? branding.banner_url : '';
  if (!url) return null;
  const alt = typeof branding?.banner_alt === 'string' ? branding.banner_alt : '';
  return { url, alt };
}

/**
 * §28.2 — organizer banner on M01/M02, 2:1, cover.
 *
 * When `banner_url` is absent the component renders nothing at all: no
 * placeholder frame, no reserved gap. An empty box at the top of the page
 * reads as a broken site, which is worse than a page that simply starts with
 * the event name.
 *
 * Decorative: never a tap target, and `alt` falls back to empty so a screen
 * reader skips it rather than announcing a filename.
 */
export function EventBanner({
  event,
  variant = 'full',
}: {
  event: PublicEvent;
  variant?: 'full' | 'thumbnail';
}) {
  const banner = bannerFrom(event);
  if (!banner) return null;

  if (variant === 'thumbnail') {
    return (
      <img
        src={banner.url}
        alt={banner.alt}
        className="h-12 w-24 shrink-0 rounded-lg object-cover"
        loading="lazy"
      />
    );
  }

  return (
    <img
      src={banner.url}
      alt={banner.alt}
      className="aspect-2/1 w-full object-cover"
      // The banner is the first paint of the page; do not defer it.
      fetchPriority="high"
    />
  );
}
